/**
 * Editing actions of the motion columns (W2-04a; DESIGN.md §5.6.1-5.6.5), shared by the commands, the pointer
 * gestures and the RPE segment placement. Every action is ONE undoable transaction over the core command bodies
 * (`Core.keys`, `Core.transform`), which keep the model invariants: shape-preserving splits, the beat-0 lead-in Stay
 * key of a new track, link groups and the Position X/Y link (mirrored by the core).
 */

import { Core } from '../../core/commands/index';
import { easeUseFor, trackInfo } from '../../core/commands/motionTrack';
import { textSegmentMode } from '../../core/commands/text';
import { Ease as EaseApi } from '../../core/ease/index';
import type { Ease, EaseUse } from '../../core/ease/types';
import { DIALOG } from '../../app/dialogs/types';
import type { KeyId, LineId, LineProp, TrackId } from '../../core/model/types';
import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';
import type { TxBuilder } from '../../core/ops/types';
import { hasGifTexture, kindOfLine, isUiMover } from '../../core/model/lineKind';
import type { ColumnRef, DocStore } from '../../state/types';
import { isLayered, layersOf, xyPartnerRef, type ColumnSpec } from './columnSpec';
import { propNames } from './format';
import { m } from './motion.messages';
import type { MotionSession } from './session';
import { newTrackValue, ref, runTx, type Services } from './txn';

/** Line kind context of a line in a transaction or store. */
export function propCtxOf(
  view: Pick<TxBuilder, 'doc' | 'idx'> | DocStore,
  line: LineId,
): { kind: ReturnType<typeof kindOfLine>; uiMover: boolean } {
  const rec = view.doc.lines.get(line);
  if (rec === undefined) return { kind: 'normal', uiMover: false };
  return { kind: kindOfLine(view.doc, view.idx, rec), uiMover: isUiMover(rec) };
}

/**
 * The curve a new segment after the end of a track gets (DESIGN.md §5.6.1: "the segment after a key at the end of a
 * track copies the last used curve", replacing RPE's "inherit previous easing"), and the level whose easing guards
 * apply (Guided Scroll speed: Linear and Stay only).
 */
export interface TailCurve {
  readonly curve: Ease;
  readonly level: 'guided' | 'full';
}

/** The tail curve of a session (the last curve the user chose, initially Linear). */
export function tailOf(s: Pick<Services, 'settings' | 'editor'>, session: MotionSession): TailCurve {
  return { curve: session.lastCurve, level: levelOf(s) };
}

/**
 * Inserts a key at `t` on (line, prop, layer) inside a transaction: shape-preserving split of an existing track
 * (Core.keys.insertKey), or a new track holding the value that renders like "no track" (Core.keys.ensureTrack adds
 * the beat-0 Stay key when the first key is later). Returns the key and the split exactness.
 *
 * With `tail`, a key appended after the last key of an existing track ends a new segment: that segment gets the
 * tail curve (on the linked X/Y partner too, where the core appended the mirrored key), as long as the curve is
 * allowed there. The lead-in Stay of a new track is never changed.
 */
export function insertOnRef(
  tx: TxBuilder,
  target: ColumnRef,
  t: Beat,
  tail?: TailCurve,
): { key: KeyId; exactness: 'exact' | 'approx' | 'bake' } {
  const layer = isLayered(target.prop) ? target.layer : 0;
  const track = tx.idx.track(target.line, target.prop, layer);
  if (track === undefined) {
    // Text, brush and GIF tracks change what the line is: only Add property (the line type dialog) creates them.
    if (lineTypeChange(tx, target.line, target.prop) !== null) return tx.fail(ref(m.lineTypeNeeded));
    const ctx = propCtxOf(tx, target.line);
    const value = newTrackValue(target.prop, layer, ctx);
    const created = Core.keys.ensureTrack(tx, {
      owner: { kind: 'line', line: target.line, prop: target.prop, layer },
      value,
      at: t,
    });
    // Linked Position X/Y (DESIGN.md §4.7): a new axis track on a layer where the other axis has none yet gets the
    // other axis too, keyed at the same beats (the core aligns a created axis with an existing one).
    const partner = xyPartnerRef(tx.doc, { line: target.line, prop: target.prop, layer });
    if (partner !== null && tx.idx.track(partner.line, partner.prop, partner.layer) === undefined) {
      Core.keys.ensureTrack(tx, {
        owner: { kind: 'line', line: partner.line, prop: partner.prop, layer: partner.layer },
        value: newTrackValue(partner.prop, partner.layer, ctx),
        at: t,
      });
    }
    const key = keyAt(tx, created, t);
    if (key === undefined) return tx.fail(ref('cmdMotion.trackMissing'));
    return { key, exactness: 'exact' };
  }
  const partner = xyPartnerRef(tx.doc, { line: target.line, prop: target.prop, layer });
  const partnerTrack = partner === null ? undefined : tx.idx.track(partner.line, partner.prop, partner.layer);
  const appended = keyAt(tx, track, t) === undefined;
  const partnerAppended = partnerTrack !== undefined && keyAt(tx, partnerTrack, t) === undefined;
  const res = Core.keys.insertKey(tx, { track, t });
  if (tail !== undefined) {
    const tails: KeyId[] = [];
    if (appended) pushTail(tx, res.key, tails);
    if (partnerAppended && tx.doc.tracks.has(partnerTrack)) {
      const pk = keyAt(tx, partnerTrack, t);
      if (pk !== undefined) pushTail(tx, pk, tails);
    }
    if (applyTailCurve(tx, tails, tail)) tx.setFocus({ beat: t, line: target.line });
  }
  return res;
}

/**
 * The line type a NEW track of `prop` would turn the line into (DESIGN.md §6.13: a text track makes a text line, a
 * brush track a paint line; GIF progress needs a GIF image), or null when the track exists already or creating it
 * leaves the line's type alone. Such tracks are created through the line type dialog only.
 */
export function lineTypeChange(
  view: Pick<TxBuilder, 'doc' | 'idx'> | DocStore,
  line: LineId,
  prop: LineProp,
): 'text' | 'paint' | 'gif' | null {
  if (prop !== 'text' && prop !== 'paint' && prop !== 'gif') return null;
  const rec = view.doc.lines.get(line);
  if (rec === undefined || (view.idx.tracksOfLine(line).get(prop)?.length ?? 0) > 0) return null;
  if (prop === 'gif') return hasGifTexture(view.doc, rec) ? null : 'gif';
  return propCtxOf(view, line).kind === prop ? null : prop;
}

/** The segment a key ends when it is the last key of its track (its previous key), if any. */
function pushTail(view: Pick<TxBuilder, 'doc' | 'idx'>, key: KeyId, out: KeyId[]): void {
  if (view.idx.nextKey(key) !== undefined) return;
  const prev = view.idx.prevKey(key);
  if (prev !== undefined && !out.includes(prev)) out.push(prev);
}

/** Structural equality of two eases. */
export function sameEase(a: Ease, b: Ease): boolean {
  switch (a.kind) {
    case 'hold':
      return b.kind === 'hold' && (a.gap === true) === (b.gap === true);
    case 'tween':
      return b.kind === 'tween' && a.id === b.id && a.left === b.left && a.right === b.right;
    case 'bezier':
      return b.kind === 'bezier' && a.p.every((v, i) => v === b.p[i]);
  }
}

/**
 * Gives the tail segments (start keys) the tail curve, skipping segments that have it already, segments whose use
 * refuses it (Guided Scroll speed, Bezier on speed, overshoot on text reveal...) and segments whose X/Y partner
 * segment at the same beat (which Core.keys.setEase re-eases too) is not a tail itself. True when a curve changed.
 */
function applyTailCurve(tx: TxBuilder, tails: readonly KeyId[], tail: TailCurve): boolean {
  if (tails.length === 0) return false;
  const set = new Set(tails);
  const apply: KeyId[] = [];
  for (const seg of tails) {
    const k = tx.doc.keys.get(seg);
    const next = tx.idx.nextKey(seg);
    const nk = next === undefined ? undefined : tx.doc.keys.get(next);
    if (k === undefined || nk === undefined) continue;
    const info = trackInfo(tx, k.track);
    if (info === null) continue;
    let use: EaseUse = easeUseFor(tx, info, k.vOut, nk.vIn);
    if (use === 'speedLegacy' && tail.level === 'guided') use = 'speedLegacyGuided';
    if (EaseApi.validateFor(tail.curve, use) !== null) continue;
    const owner = info.track.owner;
    if (owner.kind === 'line') {
      const p = xyPartnerRef(tx.doc, { line: owner.line, prop: owner.prop, layer: owner.layer });
      const pt = p === null ? undefined : tx.idx.track(p.line, p.prop, p.layer);
      const mirror = pt === undefined ? undefined : keyAt(tx, pt, k.t);
      if (mirror !== undefined && tx.idx.nextKey(mirror) !== undefined && !set.has(mirror)) continue;
    }
    if (!sameEase(k.ease, tail.curve)) apply.push(seg);
  }
  if (apply.length === 0) return false;
  Core.keys.setEase(tx, { segments: apply, ease: tail.curve });
  return true;
}

/** Key of a track at exactly beat `t`. */
export function keyAt(view: Pick<TxBuilder, 'doc' | 'idx'>, track: TrackId, t: Beat): KeyId | undefined {
  for (const id of view.idx.keysByTrack(track)) {
    const k = view.doc.keys.get(id);
    if (k !== undefined && Beats.eq(k.t, t)) return id;
  }
  return undefined;
}

/** Remembers the segments around an approximately split key (the "≈ split" badge). */
export function noteApproxSplit(session: MotionSession, store: DocStore, key: KeyId): void {
  const k = store.doc.keys.get(key);
  if (k === undefined) return;
  session.approx.set(key, k.ease);
  const prev = store.idx.prevKey(key);
  const pk = prev === undefined ? undefined : store.doc.keys.get(prev);
  if (pk !== undefined && prev !== undefined) session.approx.set(prev, pk.ease);
  session.touch();
}

/**
 * Adds keys at beat `t` on every target (one transaction) and selects them; a key after the end of a track gets the
 * last used curve on its new segment. Returns the new keys (empty when refused).
 */
export function addKeys(
  s: Services,
  session: MotionSession,
  targets: readonly ColumnRef[],
  t: Beat,
  label = m.txAddKey,
): KeyId[] {
  const store = s.store;
  if (store === null || targets.length === 0) return [];
  // A key would create a text / brush / GIF track that changes the line type: the line type dialog explains and
  // converts a single line; with several lines those are left out and a toast says so.
  const blocked = targets.filter((r) => store.doc.lines.has(r.line) && lineTypeChange(store, r.line, r.prop) !== null);
  const list = blocked.length === 0 ? targets : targets.filter((r) => !blocked.includes(r));
  const first = blocked[0];
  if (first !== undefined) {
    const change = lineTypeChange(store, first.line, first.prop);
    if (list.length === 0 && blocked.length === 1 && change !== null) {
      void s.ui.openDialog(DIALOG.lineType, { line: first.line, target: change });
      return [];
    }
    const name = s.i18n.t(propNames(first.prop, propCtxOf(store, first.line)).full);
    s.ui.toast(ref(m.lineTypeSkipped, { prop: name, count: blocked.length }), { kind: 'info' });
    if (list.length === 0) return [];
  }
  const tail = tailOf(s, session);
  const out = runTx(s, { label: ref(list.length > 1 ? m.txAddKeys : label), command: 'keys.addAtPlayhead' }, (tx) => {
    const keys: KeyId[] = [];
    const approx: KeyId[] = [];
    for (const target of list) {
      if (!tx.doc.lines.has(target.line)) continue;
      const r = insertOnRef(tx, target, t, tail);
      keys.push(r.key);
      if (r.exactness === 'approx') approx.push(r.key);
    }
    tx.setSelection({ keys, segments: [], notes: [] });
    return { keys, approx };
  });
  if (!out.ok) return [];
  for (const k of out.value.approx) noteApproxSplit(session, store, k);
  return out.value.keys;
}

/**
 * Shift+K inside a transaction: a key at `t` on every animated property of each line (Core.keys.insertKeysAt);
 * keys appended after the end of a track get the tail curve on their new segment. Returns the keys.
 */
export function insertAnimatedKeys(
  tx: TxBuilder,
  lines: readonly LineId[],
  layer: number,
  t: Beat,
  tail: TailCurve,
): KeyId[] {
  const keys: KeyId[] = [];
  const tails: KeyId[] = [];
  for (const line of lines) {
    // With several lines, lines without animated properties are skipped (the single-line case reports it).
    if (lines.length > 1 && animatedProps(tx, line, layer).length === 0) continue;
    // Tracks that had no key at `t` before (a track the core creates, e.g. a linked partner axis, keeps its
    // lead-in Stay).
    const open: TrackId[] = [];
    for (const tracks of tx.idx.tracksOfLine(line).values()) {
      for (const tr of tracks) if (keyAt(tx, tr, t) === undefined) open.push(tr);
    }
    keys.push(...Core.keys.insertKeysAt(tx, { line, props: 'animated', layer, t }));
    for (const tr of open) {
      if (!tx.doc.tracks.has(tr)) continue;
      const k = keyAt(tx, tr, t);
      if (k !== undefined) pushTail(tx, k, tails);
    }
  }
  if (applyTailCurve(tx, tails, tail)) tx.setFocus({ beat: t });
  return keys;
}

/** The properties of a line that are animated (two or more keys) on a layer. */
export function animatedProps(store: Pick<DocStore, 'doc' | 'idx'>, line: LineId, layer: number): LineProp[] {
  const out: LineProp[] = [];
  for (const [prop, tracks] of store.idx.tracksOfLine(line)) {
    for (const id of tracks) {
      const tr = store.doc.tracks.get(id);
      if (tr?.owner.kind !== 'line') continue;
      const wantLayer = isLayered(prop) ? layer : 0;
      if (tr.owner.layer === wantLayer && store.idx.keysByTrack(id).length >= 2) out.push(prop);
    }
  }
  return out;
}

/** How a segment may be eased (EaseUse guards of the easing picker and setEase; DESIGN.md §4.12, §6.11, §6.8). */
export function easeUseOf(store: DocStore, level: 'guided' | 'full', segment: KeyId): EaseUse {
  const k = store.doc.keys.get(segment);
  const tr = k === undefined ? undefined : store.doc.tracks.get(k.track);
  if (k === undefined || tr === undefined) return 'general';
  if (tr.owner.kind !== 'line') return 'extraVar';
  if (tr.owner.prop === 'speed') {
    if (store.doc.pkg.speedMode === 'rpe170') return 'speed170';
    return level === 'guided' ? 'speedLegacyGuided' : 'speedLegacy';
  }
  if (tr.owner.prop === 'text') {
    const next = store.idx.nextKey(segment);
    const nk = next === undefined ? undefined : store.doc.keys.get(next);
    const start = typeof k.vOut === 'string' ? k.vOut : '';
    const end = typeof nk?.vIn === 'string' ? nk.vIn : '';
    const mode = textSegmentMode(
      start,
      end,
      k.ease.kind === 'hold' ? { kind: 'tween', id: 1, left: 0, right: 1 } : k.ease,
    );
    return mode === 'typeOn' || mode === 'erase' ? 'textRevealErase' : 'textOther';
  }
  return 'general';
}

/** Current experience level (the settings are the single source). */
export function levelOf(s: Pick<Services, 'settings' | 'editor'>): 'guided' | 'full' {
  try {
    return s.settings.value.general.level;
  } catch {
    return s.editor.getState().level;
  }
}

/**
 * Opens the easing picker (W2-05's dialog) for segments and applies the choice (Core.keys.setEase: guards per use,
 * X/Y link mirrored, skipped segments noted). Remembers it as the last used curve.
 */
export async function openEasingPicker(
  s: Services,
  session: MotionSession,
  segments: readonly KeyId[],
): Promise<boolean> {
  const store = s.store;
  const first = segments[0];
  if (store === null || first === undefined) return false;
  if (!(await confirmGuidedKeys(s, session, segments))) return false;
  const initial = store.doc.keys.get(first)?.ease ?? null;
  const use = easeUseOf(store, levelOf(s), first);
  const chosen = await s.ui.openDialog(DIALOG.easingPicker, { segments: [...segments], initial, use });
  if (chosen === null) return false;
  const live = segments.filter((id) => store.doc.keys.has(id));
  if (live.length === 0) return false;
  const out = runTx(s, { label: ref(m.txEase), command: 'segments.easing' }, (tx) =>
    Core.keys.setEase(tx, { segments: live, ease: chosen }),
  );
  if (!out.ok) return false;
  session.lastCurve = chosen;
  return true;
}

/** Ack key of the Guided Σ column of (line, prop): the Position X | Y pair shares the one of X. */
export function guidedAckKey(line: LineId, prop: LineProp): string {
  return `${String(line)}:${prop === 'moveY' ? 'moveX' : prop}`;
}

/**
 * Layers in use of the Guided Σ column (line, prop) belongs to (DESIGN.md §2.4: a property that adds up several
 * layers shows their sum in Guided; the Position pair counts both axes), or null when there is none: Full level,
 * a property without layers, or only layer 0 in use.
 */
export function guidedSumLayers(
  s: Pick<Services, 'store' | 'settings' | 'editor'>,
  line: LineId,
  prop: LineProp,
): number[] | null {
  const store = s.store;
  if (store === null || levelOf(s) !== 'guided' || !isLayered(prop)) return null;
  const props: readonly LineProp[] = prop === 'moveX' || prop === 'moveY' ? ['moveX', 'moveY'] : [prop];
  const layers = [...new Set(props.flatMap((p) => layersOf(store.doc, store.idx, line, p)))].sort((a, b) => a - b);
  return layers.some((l) => l !== 0) ? layers : null;
}

/**
 * The Guided Σ columns an edit of `refs` would change without the user having answered the "uses N layers"
 * question for them yet: ack key -> number of layers. Edits of other layers than the base layer are explicit
 * (pinned columns of a layer) and never ask.
 */
export function guidedPending(
  s: Pick<Services, 'store' | 'settings' | 'editor'>,
  session: MotionSession,
  refs: readonly ColumnRef[],
): Map<string, number> {
  const out = new Map<string, number>();
  if (levelOf(s) !== 'guided') return out;
  for (const r of refs) {
    if (r.layer !== 0) continue;
    const ack = guidedAckKey(r.line, r.prop);
    if (session.guidedAck.has(ack) || out.has(ack)) continue;
    const layers = guidedSumLayers(s, r.line, r.prop);
    if (layers !== null) out.set(ack, Math.max(2, layers.length));
  }
  return out;
}

/** The (line, prop, layer) of each key's track (line tracks only, each once). */
export function refsOfKeys(store: Pick<DocStore, 'doc'>, keys: Iterable<KeyId>): ColumnRef[] {
  const out: ColumnRef[] = [];
  const seen = new Set<TrackId>();
  for (const id of keys) {
    const k = store.doc.keys.get(id);
    if (k === undefined || seen.has(k.track)) continue;
    seen.add(k.track);
    const owner = store.doc.tracks.get(k.track)?.owner;
    if (owner?.kind === 'line') out.push({ line: owner.line, prop: owner.prop, layer: owner.layer });
  }
  return out;
}

/**
 * Guided level, edits of a property that adds up several layers (the Σ column): the first edit asks whether to edit
 * the base layer here or to switch to Full to see the layers (DESIGN.md §2.4 "editing offers to switch to the layer
 * view"). Asked once per column ("Edit the base layer" is remembered for the project); every editing path (keys
 * added, dragged, nudged, deleted, re-eased, Stay, jumps, negate, fine-tune, reset) goes through here. True = go on.
 */
export async function confirmGuidedRefs(
  s: Services,
  session: MotionSession,
  refs: readonly ColumnRef[],
): Promise<boolean> {
  const pending = guidedPending(s, session, refs);
  if (pending.size === 0) return true;
  if (session.guidedAsking) return false;
  session.guidedAsking = true;
  let choice: 'base' | 'full' | null;
  try {
    choice = await s.ui.choose<'base' | 'full'>({
      title: ref(m.guidedLayersTitle, { n: Math.max(...pending.values()) }),
      body: ref(m.guidedLayersBody),
      options: [
        { value: 'base', label: ref(m.guidedLayersBase) },
        { value: 'full', label: ref(m.guidedLayersFull) },
      ],
    });
  } finally {
    session.guidedAsking = false;
  }
  if (choice === 'base') {
    for (const ack of pending.keys()) session.guidedAck.add(ack);
    return true;
  }
  if (choice === 'full') switchToFull(s);
  return false;
}

/** `confirmGuidedRefs` for the tracks of keys or segments (their start keys). */
export function confirmGuidedKeys(s: Services, session: MotionSession, keys: Iterable<KeyId>): Promise<boolean> {
  const store = s.store;
  if (store === null) return Promise.resolve(true);
  return confirmGuidedRefs(s, session, refsOfKeys(store, keys));
}

/** `confirmGuidedRefs` for the lanes of a column (a Guided Σ column edits its base layer). */
export function confirmGuidedEdit(s: Services, session: MotionSession, spec: ColumnSpec): Promise<boolean> {
  if (!spec.sumView || spec.level !== 'guided') return Promise.resolve(true);
  return confirmGuidedRefs(
    s,
    session,
    spec.lanes.map((l) => ({ line: spec.line, prop: l.prop, layer: 0 })),
  );
}

/** Switches the experience level to Full (settings are the single source; the editor store mirrors them). */
export function switchToFull(s: Pick<Services, 'settings' | 'editor'>): void {
  s.editor.getState().setLevel('full');
  try {
    if (s.settings.value.general.level !== 'full') s.settings.set('general', 'level', 'full');
  } catch {
    // settings unavailable: the editor mirror is set
  }
}
